import t from '../helpers/calculator_translations';
import range from '../helpers/range';
import type { Advantage, CalculatorResponsePrice } from '../interfaces/Calculator.model';
import type { CalculatorConfig } from './CalculatorConfigGroup';
import { InfoPopup } from './InfoPopup';

interface CalculatorResultSetProps {
    calculatorConfig: CalculatorConfig;
    locale: string;
    data: CalculatorResponsePrice;
    wordRepetitionDiscountInfoText?: string;
    welcomeDiscountInfoText?: string;
    sourceLanguage: string;
    targetLanguage: string;
    type: 'ai' | 'excellent' | 'economy' | 'express';
    advantages: Advantage[];
    maxAdvantages: number;
    minimumWordPrice: string | null;
    calculated: boolean;
}

export const CalculatorResultSet = ({
    calculatorConfig,
    locale,
    data,
    wordRepetitionDiscountInfoText,
    welcomeDiscountInfoText,
    sourceLanguage,
    targetLanguage,
    type,
    advantages,
    maxAdvantages,
    minimumWordPrice,
    calculated,
}: CalculatorResultSetProps): JSX.Element => {
    const qualityLevelMap = {
        ai: 'postediting',
        excellent: 'excellent',
        economy: 'economy',
        express: 'express',
    };

    const params = new URLSearchParams({
        sl: sourceLanguage,
        tl: targetLanguage,
        ql: qualityLevelMap[type] ?? '',
    });

    const estimateUrl = `${calculatorConfig.estimateUrl}?${params.toString()}`;
    const totalPriceAfterDiscountInclVat =
        (data.totalPriceAfterDiscount * (100 + calculatorConfig.vatRate)) / 100;

    const filteredAdvantages = advantages.filter((advantage) => advantage[type]);

    const extraAdvantagesToFillUp = maxAdvantages - filteredAdvantages.length;

    return (
        <div
            key={type}
            className={`relative w-100 card text-center calculator__result card-${type}`}
        >
            {type === 'excellent' && (
                <div className={'bestseller'}>
                    <span
                        className={
                            '' +
                            'badge-pill rounded-pill bg-dark text-light ' +
                            'py-1 px-3 py-lg-2 px-lg-4'
                        }
                        style={{}}
                    >
                        {t('calculator.bestseller')}
                    </span>
                </div>
            )}

            <div className={'image-container'}>
                <img src={`/images/calculator/${type}.png`} alt={type} />
            </div>
            <div className={'pt-4 pb-2 px-3'}>
                <div className={'h3 calculator__result__title mb-3'}>{data.heading}</div>

                {!calculated && minimumWordPrice && (
                    <div
                        className={
                            'd-flex justify-content-between align-items-end flex-md-wrap pb-4'
                        }
                    >
                        <div
                            className={
                                'w-100 mb-md-3 calculator__result__empty-state-word-price text-tertiary'
                            }
                        >
                            <span>{t('calculator.word_price_from')}</span>
                            <br />
                            {new Intl.NumberFormat(locale, {
                                style: 'currency',
                                currency: 'EUR',
                            }).format(parseFloat(minimumWordPrice))}
                        </div>
                    </div>
                )}

                <ul className={'mb-3 list-unstyled calculator__result__advantages'}>
                    {filteredAdvantages?.map((advantage: Advantage, index: number) => (
                        <li
                            key={type + index}
                            className={`${
                                advantage[type] ? 'advantages__checkmark me-1' : 'advantages__close'
                            } me-1`}
                        >
                            <em
                                className={`fa ${advantage[type] ? 'text-success fa-check' : 'fa-close'}`}
                            ></em>{' '}
                            {advantage.usp}
                            {advantage.infoText && <InfoPopup text={advantage.infoText} />}
                        </li>
                    ))}
                    {range(extraAdvantagesToFillUp).map((item: number) => (
                        <li key={item}>&nbsp;</li>
                    ))}
                </ul>

                {calculated && (
                    <div className={'d-flex justify-content-between align-items-end flex-wrap'}>
                        <div className={'w-100'}>
                            <span
                                className={
                                    'd-inline-block pb-1 calculator__result__total_price_label'
                                }
                            >
                                {data.minimumPriceActive
                                    ? t('calculator.price.minimum')
                                    : t('calculator.total_price')}
                            </span>
                            <br />
                            <span className={'calculator__result__price text-tertiary'}>
                                {data.totalPriceAfterDiscount ? (
                                    new Intl.NumberFormat(locale, {
                                        style: 'currency',
                                        currency: 'EUR',
                                    }).format(data.totalPriceAfterDiscount)
                                ) : (
                                    <span>{t('calculator.price.in_consultation')}</span>
                                )}
                            </span>
                        </div>

                        {!data.minimumPriceActive && data.pricePerWord && (
                            <div
                                className={
                                    'w-100 d-none d-md-block mb-3 calculator__result__price_details calculator__result__price_per_word'
                                }
                            >
                                (
                                {new Intl.NumberFormat(locale, {
                                    style: 'currency',
                                    currency: 'EUR',
                                }).format(data.pricePerWord)}{' '}
                                {t('calculator.price.per_word')})
                            </div>
                        )}

                        <div
                            className={
                                'w-100 d-none d-lg-block mb-3 text-muted calculator__result__price_details py-2'
                            }
                        >
                            {data.wordRepetitionDiscount ? (
                                <>
                                    {t('calculator.word_repetitions')}:{' '}
                                    {data.wordRepetitionDiscount}% ({t('calculator.average')})
                                    <InfoPopup text={wordRepetitionDiscountInfoText ?? ''} />
                                </>
                            ) : (
                                <>&nbsp;</>
                            )}

                            <div className={!data.welcomeDiscount ? 'd-none' : ''}>
                                {t('calculator.welcome_discount')}: {data.welcomeDiscount}%
                                <InfoPopup text={welcomeDiscountInfoText ?? ''} />
                            </div>

                            <div>
                                {t('calculator.delivery_time')}:{' '}
                                {data.deliveryTime ? (
                                    <>
                                        {data.deliveryTime}{' '}
                                        {data.deliveryTime === 1
                                            ? t('calculator.delivery_time.working_day')
                                            : t('calculator.delivery_time.working_days')}
                                    </>
                                ) : (
                                    t('calculator.delivery_time.in_consultation')
                                )}
                            </div>
                        </div>

                        <div className={'w-100 mb-md-3'}>
                            <a className={'btn btn-lg btn-primary text-light'} href={estimateUrl}>
                                <span className={'d-none d-lg-inline'}>
                                    {t('calculator.buttons.request_estimate')}
                                </span>
                                <span className={'d-lg-none'}>
                                    {t('calculator.buttons.request_estimate_short')}
                                </span>
                            </a>
                        </div>
                    </div>
                )}

                {data?.text && (
                    <div className={'py-1 pt-3 pt-sm-1'}>
                        <InfoPopup text={data?.text}>
                            <span
                                style={{
                                    cursor: 'pointer',
                                    fontSize: '0.95rem',
                                    lineHeight: '1.3rem',
                                    textDecoration: 'underline',
                                }}
                            >
                                {t('calculator.more_information')}
                            </span>
                        </InfoPopup>
                    </div>
                )}
            </div>

            {calculatorConfig.showPriceInclVat && (
                <div
                    className={
                        'text-center w-100 mb-3 calculator__result__price_details calculator__result__price_excl_vat'
                    }
                >
                    {t('calculator.exclude_vat', undefined, {
                        vat_price: new Intl.NumberFormat(locale, {
                            style: 'currency',
                            currency: 'EUR',
                        }).format(totalPriceAfterDiscountInclVat),
                    })}
                    <br />
                    {t('calculator.indicative_calculation')}
                </div>
            )}
        </div>
    );
};
